import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { addPaddingZerosToNum } from 'utils/functions/general';
import { status } from 'constants/general';
import { loginFormStepsEnum } from 'modules/accountManagement/enums';
import accountManagementStrings from 'modules/accountManagement/strings';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';
import OTPInputField from 'modules/common/components/OTPInputField';

import {
  Wrapper,
  Title,
  SubTitle,
  Info,
  InfoBtn,
  InputWrapper,
  Input,
  ValidationErrMsg,
  SubmitBtn,
  SignUpBtn,
  DisclaimerInfo,
} from './LoginComponent.style';

import './style.css';

const UserInformation = ({
  isSignUp,
  formFieldsValues,
  formFieldsValidation,
  userAccessError,
  handleTextInputFieldChange,
  handleSignUpBtnClick,
  handleSubmitUserInformationBtnClick,
}) => {
  const { requestOtpInfo } = useSelector(state => ({
    requestOtpInfo: state.accountManagement.requestOtpInfo,
  }));

  const formTitle =
    (isSignUp && accountManagementStrings.signUpForm.userInformationFormTitle) ||
    accountManagementStrings.loginForm.userInformationFormTitle;
  const formSubTitle =
    (isSignUp && accountManagementStrings.signUpForm.userInformationFormSubTitle) ||
    accountManagementStrings.loginForm.userInformationFormSubTitle;
  const submitBtnText =
    (isSignUp && accountManagementStrings.signUpForm.userInformationFormSubmitBtn) ||
    accountManagementStrings.loginForm.userInformationFormSubmitBtn;

  return (
    <Wrapper>
      <Title>{formTitle}</Title>
      <SectionDivider gap={4} />
      <SubTitle>{formSubTitle}</SubTitle>
      <SectionDivider gap={45} />
      <InputWrapper icon="'\e942'" enableLeadingText>
        <Input
          value={formFieldsValues.phone}
          type="number"
          placeholder={accountManagementStrings.loginForm.userInformationFormPhoneFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('phone', event)}
        />
      </InputWrapper>
      {formFieldsValidation.phone === false && (
        <ValidationErrMsg>
          {accountManagementStrings.loginForm.userInformationFormFieldErrorText(
            accountManagementStrings.loginForm.userInformationFormPhoneFieldLabel,
          )}
        </ValidationErrMsg>
      )}
      {userAccessError === true && (
        <ValidationErrMsg>{accountManagementStrings.loginForm.userAccessError}</ValidationErrMsg>
      )}
      <SectionDivider gap={15} />
      <InputWrapper icon="'\e903'">
        <Input
          value={formFieldsValues.name}
          placeholder={accountManagementStrings.loginForm.userInformationFormNameFieldPlaceholder}
          onChange={event => handleTextInputFieldChange('name', event)}
        />
      </InputWrapper>
      {formFieldsValidation.name === false && (
        <ValidationErrMsg>
          {accountManagementStrings.loginForm.userInformationFormFieldErrorText(
            accountManagementStrings.loginForm.userInformationFormNameFieldPlaceholder,
          )}
        </ValidationErrMsg>
      )}
      <SectionDivider gap={35} />
      {(requestOtpInfo.status === status.FETCHING && (
        <Loader width={30} height={30} thickness={5} />
      )) || (
        <SubmitBtn as="button" onClick={() => handleSubmitUserInformationBtnClick()}>
          {submitBtnText}
        </SubmitBtn>
      )}
      {isSignUp === false && (
        <>
          <SectionDivider gap={45} />
          <SignUpBtn as="button" onClick={handleSignUpBtnClick}>
            {accountManagementStrings.loginForm.userInformationFormSignUpBtn}
          </SignUpBtn>
        </>
      )}
    </Wrapper>
  );
};
UserInformation.propTypes = {
  isSignUp: PropTypes.bool,
  formFieldsValues: PropTypes.shape({
    phone: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    phone: PropTypes.bool,
    name: PropTypes.bool,
  }).isRequired,
  userAccessError: PropTypes.bool.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleSignUpBtnClick: PropTypes.func.isRequired,
  handleSubmitUserInformationBtnClick: PropTypes.func.isRequired,
};
UserInformation.defaultProps = {
  isSignUp: false,
};

const UserOTP = ({
  formFieldsValues,
  otpValue,
  otpError,
  otpSeconds,
  handleOTPInputFieldChange,
  handleChangePhoneBtnClick,
  handleSendAgainBtnClick,
  handleSubmitUserOtpBtnClick,
}) => {
  const { signWithOtpInfo } = useSelector(state => ({
    signWithOtpInfo: state.accountManagement.signWithOtpInfo,
  }));

  const otpSecondsFormatted = addPaddingZerosToNum(otpSeconds, 1, 2);
  const isSubmitBtnDisabled = otpValue.length !== 6;

  return (
    <Wrapper>
      <Title>{accountManagementStrings.loginForm.userOtpFormTitle}</Title>
      <SectionDivider gap={4} />
      <SubTitle>{accountManagementStrings.loginForm.userOtpFormSubTitle}</SubTitle>
      <SectionDivider gap={2} />
      <Info>
        {formFieldsValues.phone}
        <InfoBtn onClick={handleChangePhoneBtnClick}>
          {accountManagementStrings.loginForm.userOtpFormChangePhoneBtn}
        </InfoBtn>
      </Info>
      <SectionDivider gap={45} />
      <OTPInputField
        onChange={handleOTPInputFieldChange}
        numInputs={6}
        separator={<span>-</span>}
        inputStyle="otp-input-item"
        containerStyle="otp-container"
        shouldAutoFocus
        value={otpValue}
        errorStyle="otp-input-item-error"
        hasErrored={otpError}
      />
      {otpError && (
        <ValidationErrMsg>
          {accountManagementStrings.loginForm.userOtpFormWrongOtpError}
        </ValidationErrMsg>
      )}
      {(otpSeconds <= 0 && (
        <Info>
          {accountManagementStrings.loginForm.userOtpFormChangePhoneText}
          <InfoBtn onClick={handleSendAgainBtnClick}>
            {accountManagementStrings.loginForm.userOtpFormSendAgainBtn}
          </InfoBtn>
        </Info>
      )) || (
        <Info>{`${accountManagementStrings.loginForm.userOtpFormSendAgainText} 0:${otpSecondsFormatted}`}</Info>
      )}
      <SectionDivider gap={35} />
      {(signWithOtpInfo.status === status.FETCHING && (
        <Loader width={30} height={30} thickness={5} />
      )) || (
        <SubmitBtn
          as="button"
          disabled={isSubmitBtnDisabled}
          onClick={() => handleSubmitUserOtpBtnClick()}
        >
          {accountManagementStrings.loginForm.userOtpFormSubmitBtn}
        </SubmitBtn>
      )}
      <SectionDivider gap={15} />
      <DisclaimerInfo>
        {accountManagementStrings.loginForm.userOtpFormDisclaimerText}
      </DisclaimerInfo>
      <DisclaimerInfo>{accountManagementStrings.loginForm.userOtpFormTermsText}</DisclaimerInfo>
    </Wrapper>
  );
};
UserOTP.propTypes = {
  formFieldsValues: PropTypes.shape({
    phone: PropTypes.string,
  }).isRequired,
  otpValue: PropTypes.string.isRequired,
  otpError: PropTypes.bool.isRequired,
  otpSeconds: PropTypes.number.isRequired,
  handleOTPInputFieldChange: PropTypes.func.isRequired,
  handleChangePhoneBtnClick: PropTypes.func.isRequired,
  handleSendAgainBtnClick: PropTypes.func.isRequired,
  handleSubmitUserOtpBtnClick: PropTypes.func.isRequired,
};

const LoginComponent = props => {
  const { currentStep } = props;

  return (
    (currentStep === loginFormStepsEnum.loginInfo && <UserInformation {...props} />) || (
      <UserOTP {...props} />
    )
  );
};

LoginComponent.propTypes = {
  currentStep: PropTypes.number.isRequired,
};

export default LoginComponent;
